// src/components/EmissaryOptimized.tsx
"use client"
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { User } from 'firebase/auth';
import type { Document, UserState } from '@/types';
import type { SelectedItem } from '@/types/file-browser';
import LoginDialog from "@/components/LoginDialog";
import { useToast } from "@/components/hooks/use-toast";
import { OptimizedContextProvider } from '@/contexts/OptimizedContextProvider';
import ChatInterface from '@/components/ChatInterface';
import type { AccessControl } from '@/lib/firebase';
import type { MessageContext } from '@/types/chat';
import { validateMessageForSending } from '@/utils/sessionManager';
import { convertFirestoreMessagesToLocal } from '@/utils/emissaryHelpers';

// Import the new optimized contexts
import { useMessageState } from '@/contexts/MessageStateContext';
import { useStreaming } from '@/contexts/StreamingContext';
import { useToolState } from '@/contexts/ToolStateContext';
import { useToolActions } from '@/contexts/ToolActionsContext';
import { useAssistant } from '@/contexts/AssistantContext';

export type EmissaryOptimizedProps = {
  name?: string;
  avatar?: string;
  assistantId: string;
  initialMessage?: string;
  initialDocuments?: Document[];
  initialInstructions?: string;
  adminEmail?: string;
  senderName?: string;
  userState: UserState;
  currentUser: User | null;
  showLoginDialog: boolean;
  setShowLoginDialog: (show: boolean) => void;
  handleLogout: () => Promise<void>;
  tools?: string[];
  toolConfigs?: Record<string, Record<string, any>>;
  accessControl?: AccessControl;
  templateId?: string;
};

// Inner component that uses all the optimized contexts
interface EmissaryInnerProps {
  currentUser: User | null;
  userState: UserState;
  showLoginDialog: boolean;
  setShowLoginDialog: (show: boolean) => void;
}

const EmissaryInner = React.memo<EmissaryInnerProps>(({
  currentUser,
  userState,
  showLoginDialog,
  setShowLoginDialog
}) => {
  const { toast } = useToast();
  const { config } = useAssistant();
  
  // Use optimized contexts
  const { messages } = useMessageState();
  const { 
    streamingMessage, 
    effectiveThinkingContent,
    isStreaming, 
    error,
    sendMessage: contextSendMessage,
    stopStreaming,
    sessionId,
    traceId
  } = useStreaming();
  const { confirmedTools } = useToolState();
  const { processToolConfirmation, isProcessingConfirmation, abortToolConfirmation } = useToolActions();
  
  const [input, setInput] = useState('');
  
  // Enhanced stop streaming that also aborts tool confirmations
  const handleStopStreaming = useCallback(() => {
    // Stop the regular streaming
    stopStreaming();
    
    // Also abort any ongoing tool confirmation
    if (isProcessingConfirmation) {
      abortToolConfirmation();
    }
  }, [stopStreaming, abortToolConfirmation, isProcessingConfirmation]);
  
  // Process tool confirmations when they're ready - memoized config
  const configWithCurrentUser = useMemo(() => ({
    assistantId: config.assistantId,
    templateId: config.templateId,
    accessControl: config.accessControl,
    name: config.name,
    avatar: config.avatar,
    initialInstructions: config.initialInstructions,
    initialDocuments: config.initialDocuments,
    tools: config.tools,
    toolConfigs: config.toolConfigs,
    adminEmail: config.adminEmail,
    senderName: config.senderName
  }), [config]);

  useEffect(() => {
    if (confirmedTools !== null && !isStreaming) {
      processToolConfirmation(configWithCurrentUser);
    }
  }, [confirmedTools, isStreaming, configWithCurrentUser, processToolConfirmation]);
  
  // Handle sending messages
  const handleSendMessage = useCallback(async (messageContext: MessageContext) => {
    const { text } = messageContext;
    
    const validation = validateMessageForSending(text, currentUser ? { email: currentUser.email || undefined } : null, config.assistantId);
    if (!validation.isValid) {
      if (validation.reason === 'User not authenticated') {
        toast({ 
          title: "Login Required",
          description: "Please login to send messages",
          variant: "destructive" 
        });
        setShowLoginDialog(true);
      }
      return;
    }
    
    setInput('');
    await contextSendMessage(text);
  }, [currentUser, config.assistantId, contextSendMessage, toast, setShowLoginDialog]);
  
  // Convert Firestore messages to local format for ChatInterface - memoized
  const humanMessagesToDisplay = useMemo(() => {
    return convertFirestoreMessagesToLocal(messages);
  }, [messages]);
  
  // Guard against null currentUser - ChatInterface requires it
  if (!currentUser) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <p>Please log in to continue</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="relative flex-1 overflow-hidden w-full">
      <div className="absolute inset-0 font-serif">
        <ChatInterface
          messages={humanMessagesToDisplay}
          streamingMessage={streamingMessage}
          streamingThinkingContent={effectiveThinkingContent}
          input={input}
          botName={config.name}
          botAvatar={config.avatar}
          senderName={config.senderName || 'Owner'}
          userState={userState}
          onInputChange={setInput}
          onSendMessage={handleSendMessage}
          onLogin={() => setShowLoginDialog(true)}
          isStreaming={isStreaming}
          error={error}
          tools={config.tools}
          toolConfigs={config.toolConfigs}
          currentUser={currentUser}
          assistantId={config.assistantId}
          onStopStreaming={handleStopStreaming}
          traceId={traceId || ''}
          sessionId={sessionId}
        />
      </div>

      <LoginDialog
        open={showLoginDialog}
        onOpenChange={setShowLoginDialog}
      />
    </div>
  );
});

EmissaryInner.displayName = 'EmissaryInner';

// Main component that provides all optimized contexts
export default function EmissaryOptimized(props: EmissaryOptimizedProps) {
  // Memoize assistant config to prevent unnecessary re-renders
  const assistantConfig = useMemo(() => ({
    name: props.name || "Assistant",
    avatar: props.avatar || "/placeholder.svg?height=40&width=40",
    initialMessage: props.initialMessage || "Hello!",
    initialInstructions: props.initialInstructions || "Default instructions.",
    initialDocuments: props.initialDocuments || [],
    tools: props.tools || [],
    toolConfigs: props.toolConfigs || {},
    templateId: props.templateId,
    accessControl: props.accessControl,
    adminEmail: props.adminEmail,
    senderName: props.senderName || "Owner"
  }), [
    props.name,
    props.avatar,
    props.initialMessage,
    props.initialInstructions,
    props.initialDocuments,
    props.tools,
    props.toolConfigs,
    props.templateId,
    props.accessControl,
    props.adminEmail,
    props.senderName
  ]);
  
  return (
    <OptimizedContextProvider
      assistantId={props.assistantId}
      assistantConfig={assistantConfig}
      currentUser={props.currentUser}
      selectedItems={[]} // Empty initial selectedItems, managed by context
    >
      <EmissaryInner 
        currentUser={props.currentUser}
        userState={props.userState}
        showLoginDialog={props.showLoginDialog}
        setShowLoginDialog={props.setShowLoginDialog}
      />
    </OptimizedContextProvider>
  );
}